<template>
  <!-- Child-school side of the fee-split dual opt-in. Renders nothing for
       standalone/parent schools — only a campus (child school) ever consents. -->
  <div
    v-if="isChild"
    class="bg-white rounded-xl border border-gray-200 overflow-hidden"
    data-testid="fee-split-consent-card"
  >
    <div class="px-5 py-4 border-b border-gray-100">
      <h2 class="text-base font-bold text-gray-900">Fee splitting with {{ parentName }}</h2>
      <p class="text-xs text-gray-500 mt-0.5">
        Your parent school can share fee payments between itself and your campus. Nothing is shared
        without your consent below — you can withdraw it at any time.
      </p>
    </div>
    <div class="p-5 flex flex-col sm:flex-row sm:items-center gap-3">
      <template v-if="!parentEnabled">
        <Message severity="secondary" size="small" class="flex-1" data-testid="fee-split-consent-parent-off">
          {{ parentName }} has not turned on fee splitting yet, so there is nothing to consent to
          right now. If you expect this to be active, ask their administrator.
        </Message>
      </template>
      <template v-else-if="consented">
        <Message severity="success" size="small" class="flex-1" data-testid="fee-split-consent-given">
          You have consented — fee payments at your campus are shared according to
          {{ parentName }}'s split profiles.
        </Message>
        <Button
          label="Withdraw consent"
          severity="secondary"
          outlined
          size="small"
          :loading="saving"
          data-testid="fee-split-consent-withdraw"
          @click="confirmWithdraw()"
        />
      </template>
      <template v-else>
        <Message severity="warn" size="small" class="flex-1" data-testid="fee-split-consent-pending">
          {{ parentName }} has turned on fee splitting and is waiting for your consent. Until you
          consent, all payments stay with your campus as usual.
        </Message>
        <Button
          label="Give consent"
          icon="pi pi-check"
          size="small"
          :loading="saving"
          data-testid="fee-split-consent-give"
          @click="setConsent(true)"
        />
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useQuery, useMutation } from '@vue/apollo-composable';
import { useToast } from 'primevue/usetoast';
import { useConfirm } from 'primevue/useconfirm';
import { GET_FEE_SPLIT_FLAGS, SET_FEE_SPLIT_OPT_IN } from '@/graphql/billing/feeSplits';
import { withdrawConsentMessage } from '@/composables/billing/feeSplitSetup';
import { toUserMessage } from '@/utils/error';

const toast = useToast();
const confirm = useConfirm();

const { result } = useQuery(GET_FEE_SPLIT_FLAGS, {}, { fetchPolicy: 'cache-and-network' });
const school = computed(() => result.value?.getSchool ?? null);
const isChild = computed(() => school.value?.is_child === true);
const consented = computed(() => school.value?.fee_split_enabled === true);
const parentEnabled = computed(() => school.value?.parentSchool?.fee_split_enabled === true);
const parentName = computed(() => school.value?.parentSchool?.name ?? 'your parent school');

const { mutate: setFeeSplitOptIn, loading: saving } = useMutation(SET_FEE_SPLIT_OPT_IN);

const setConsent = async(enabled: boolean) => {
  try {
    await setFeeSplitOptIn({ enabled });
    toast.add({
      severity: 'success',
      summary: enabled ? 'Consent given' : 'Consent withdrawn',
      detail: enabled
        ? `Fee payments at your campus will now be shared with ${parentName.value} by its split profiles.`
        : 'Payments stay with your campus — nothing will be shared.',
      life: 5000,
    });
  } catch (e: unknown) {
    toast.add({
      severity: 'error',
      summary: enabled ? 'Couldn\'t give consent' : 'Couldn\'t withdraw consent',
      detail: toUserMessage(e, 'Nothing was changed. Please try again.'),
      life: 7000,
    });
  }
};

function confirmWithdraw() {
  confirm.require({
    header: 'Withdraw consent?',
    message: withdrawConsentMessage(parentName.value),
    icon: 'pi pi-exclamation-triangle',
    acceptLabel: 'Withdraw consent',
    rejectProps: { label: 'Keep sharing', severity: 'secondary', outlined: true },
    acceptClass: 'p-button-danger',
    accept: () => { void setConsent(false); },
  });
}
</script>
